import React, { useMemo } from 'react';
import moment from 'moment-timezone';
import { Box, Text, Heading } from '../../ui';
import { withStyles } from '../../ui/styling';
import { capitalize } from '../../ui/utils';
import { TextData, DataListRenderer, ItemRow, ITEM_ROW_HEIGHT } from '../../components';
import { ListDisplayTypes } from '../../constants';
import { apiHelpers, getAddressLines } from '../../utils';
import { NULL_STYLE } from '../../ui/system';
import { useEventCallback } from '../../ui/hooks';

/* Submit Claim's legacy list of policies, moved unchanged out of PoliciesListView when the
   Policies list became a shadcn table (UND-1133). */

const FILTERS_BAR_HEIGHT = 55;

// item xs: 296 and 316, sm: 186
export const PolicyList = React.forwardRef((props, ref) => {
  const { styles, displayType = ListDisplayTypes.CARDS, ...rest } = props;
  const Component = displayType === ListDisplayTypes.TABLE ? PolicyRowItem : PolicyListItem;
  return (
    <DataListRenderer
      ref={ref}
      itemComponent={Component}
      emptyMessage="No policies to show"
      displayType={displayType}
      itemPath="/policies"
      styles={[
        {
          root: {
            minHeight: 400,
            padTop: ({ theme }) => theme.sizes.appBarHeight + theme.spacing(1),
          },
          item: {
            height: displayType === ListDisplayTypes.CARDS ? ({ theme }) => theme.breakpoints({ xs: 312, sm: 186 }) : ITEM_ROW_HEIGHT,
            mb: displayType === ListDisplayTypes.CARDS ? '$3' : 0,
          },
          listLoading: {
            justifyContent: 'center',
          },
          header: {
            height: ({ theme }) =>
              theme.breakpoints({
                xs: 186 + theme.sizes.appBarHeight + theme.spacing(1),
                sm: 154 + theme.sizes.appBarHeight + FILTERS_BAR_HEIGHT + (displayType === ListDisplayTypes.TABLE ? ITEM_ROW_HEIGHT : 0),
              }),
          },
        },
        styles,
      ]}
      {...rest}
    />
  );
});

const PolicyRowItem = React.forwardRef(function PolicyRowItem(props, ref) {
  const { item, styles, onPress, ...rest } = props;
  const handleOnPress = useEventCallback(() => {
    onPress(item);
  });
  const policy = useFormatPolicyData(item);

  return <ItemRow ref={ref} item={policy} onPress={onPress ? handleOnPress : undefined} {...rest} />;
});

function useFormatPolicyData(policy = {}) {
  const { product: p, carrier, address, region, postcode, agent, broker } = policy;

  const product = useMemo(() => {
    return apiHelpers.resolveProduct(p);
  }, [p]);

  const addressLines = getAddressLines(address, region, postcode);

  const agentName = agent && agent.name ? agent.name.split('@')[0] : agent && agent.email ? agent.email.split('@')[0] : 'N/A';
  const brokerName = broker && broker.name ? broker.name : 'N/A';
  const carrierName = carrier && carrier.name ? carrier.name : 'N/A';
  return {
    ...policy,
    policyNumber: policy.policy_number,
    agent: {
      ...agent,
      name: agentName,
    },
    broker: {
      ...broker,
      name: brokerName,
    },
    carrier: {
      ...carrier,
      name: carrierName,
    },
    product,
    addressLines,
  };
}

export const PolicyListItem = withStyles(({ theme }) => ({
  root: {
    width: '100%',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignSelf: 'stretch',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: theme.colors.opacity('$gray.300', 0.3),
    px: theme.spacing(3),
    py: theme.spacing(2.75),
    bg: theme.colors.white,
  },
  progressOverlay: {
    ...theme.layout.absoluteFill,
    bottom: 0,
    top: null,
    height: 3,
    borderRadius: 6,
  },
  topContainer: {
    flexDirection: theme.breakpoints({ xs: 'column', sm: 'row' }),
    justifyContent: theme.breakpoints({ xs: 'flex-start', sm: 'space-between' }),
  },
  statusChips: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    alignSelf: 'flex-start',
    padBottom: theme.spacing(1),
    padTop: theme.spacing(0.5),
    props: {
      gap: 8,
    },
  },
  contentLeft: {
    flex: theme.breakpoints({ xs: NULL_STYLE, sm: 1.25 }),
    props: {
      gap: 0,
    },
  },
  contentRight: {
    flex: theme.breakpoints({ xs: NULL_STYLE, sm: 0.75 }),
    justifyContent: 'flex-start',
    alignItems: theme.breakpoints({ xs: 'flex-start', sm: 'flex-end' }),
    props: {
      gap: 12,
    },
    padTop: theme.spacing(1),
  },
  bottomContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    flex: 1,
    alignItems: 'flex-start',
    alignSelf: 'flex-start',
    padTop: theme.spacing(1.5),
  },
  bottomSpacing: {
    flexGrow: 0.1,
    flexShrink: 0,
    flexBasis: '10%',
  },
  bottomContent: {
    flexGrow: 0.9,
    flexShrink: 0,
    flexBasis: 'auto',
  },
}))(
  React.forwardRef(function PolicyListItem(props, ref) {
    const { item: data, styles, onPress, ...rest } = props;
    const {
      insured,
      product,
      addressLines,
      effectiveAt,
      effectiveUntil,
      locations = [],
      payoutsEnabled,
      canceledAt,
      policyNumber,
    } = useFormatPolicyData(data);

    const handleOnPress = useEventCallback(() => {
      onPress(data);
    });

    return (
      <Box ref={ref} onPress={onPress ? handleOnPress : undefined} accessibilityRole="link" accessibilityLabel={insured} {...rest}>
        <Box style={styles.topContainer} {...styles.props.topContainer}>
          <Box style={styles.contentLeft} {...styles.props.contentLeft}>
            <ActiveStatus payoutsEnabled={payoutsEnabled} canceledAt={canceledAt} />
            <Heading level={4} maxLines={1}>
              {capitalize(insured)}
            </Heading>
            {policyNumber && (
              <Text small color="$gray.600">
                Policy No. #{policyNumber}
              </Text>
            )}
            {Array.isArray(addressLines) ? (
              addressLines.map((a, i) => (
                <Text small key={`aline${i}`} maxLines={1}>
                  {a}
                </Text>
              ))
            ) : (
              <Text small maxLines={2}>
                {addressLines}
              </Text>
            )}
            <Box flex={1} justifyContent="flex-end">
              <Text small bold uppercase>
                {product.labels.name}
              </Text>
            </Box>
          </Box>
          <Box style={styles.contentRight} {...styles.props.contentRight}>
            <Text large bold>{`${locations.length} Location${locations.length > 1 ? 's' : ''}`}</Text>
            <TextData label="Effective From" data={moment.utc(effectiveAt).format('MM-DD-YYYY')} alignItems="inherit" size="small" />
            <TextData
              label="Effective Until"
              data={moment.utc(effectiveUntil).format('MM-DD-YYYY')}
              alignItems="inherit"
              size="small"
              opacity={0.8}
            />
          </Box>
        </Box>
      </Box>
    );
  })
);
const ActiveStatus = ({ payoutsEnabled, hasPolicyIVE, canceledAt }) => {
  const isCancelled = canceledAt != null && canceledAt <= Date.now();
  const dotColor = isCancelled ? '$gray.500' : payoutsEnabled ? '$green' : '$coral.bright';
  const label = isCancelled ? 'Cancelled' : payoutsEnabled ? 'Active' : 'Inactive';
  return (
    <Box flexDirection="row" alignItems="center" gap={12} paddingY="23px">
      <Box size="10" bg={dotColor} borderRadius="$circle" />
      <Text size="small" color={dotColor} style={{ fontWeight: 600 }} padTop={2} uppercase>
        {label}
      </Text>
      {hasPolicyIVE && payoutsEnabled && !isCancelled && (
        <Box
          style={{
            borderRadius: 6,
            backgroundColor: '#1E3A5F29',
            display: 'inline-flex',
            alignItems: 'center',
            paddingY: '2px',
            paddingX: '8px',
            marginLeft: '8px',
          }}
        >
          <Text size="xSmall" style={{ fontWeight: 700, color: '#1E3A5F' }}>
            IVE
          </Text>
        </Box>
      )}
    </Box>
  );
};
